import { http } from "@/lib/http";

import type {
  Behaviour,
  BehaviourRequest,
  BehaviourUpdateRequest,
  CustomDomain,
  CustomDomainCertificateRequest,
  CustomDomainCreateRequest,
  Distribution,
  DistributionCreateRequest,
  DistributionListParams,
  DistributionLogs,
  DistributionLogsQuery,
  DistributionMetrics,
  DistributionPage,
  DistributionUpdateRequest,
  Invalidation,
  InvalidationCreateRequest,
  OriginCandidates,
} from "./types";

/** Every distribution in the project, as the bare list (global search). */
export async function listDistributions(): Promise<Distribution[]> {
  const { data } = await http.get<Distribution[]>("/cdn/distributions");
  return data;
}

/** One page, filtered by the server, with an exact total and the location's
 *  name (present even when the project has no distribution yet). */
export async function listDistributionPage(
  params: DistributionListParams,
): Promise<DistributionPage> {
  const { data } = await http.get<DistributionPage>("/cdn/distributions", { params });
  return data;
}

/** The project's buckets, load balancers and servers as origins, each with
 *  whether it can be used and, if not, why. */
export async function listOriginCandidates(): Promise<OriginCandidates> {
  const { data } = await http.get<OriginCandidates>("/cdn/distributions/origin-candidates");
  return data;
}

export async function getDistribution(distributionId: string): Promise<Distribution> {
  const { data } = await http.get<Distribution>(`/cdn/distributions/${distributionId}`);
  return data;
}

export async function createDistribution(
  payload: DistributionCreateRequest,
): Promise<Distribution> {
  const { data } = await http.post<Distribution>("/cdn/distributions", payload);
  return data;
}

export async function updateDistribution(
  distributionId: string,
  payload: DistributionUpdateRequest,
): Promise<Distribution> {
  const { data } = await http.put<Distribution>(
    `/cdn/distributions/${distributionId}`,
    payload,
  );
  return data;
}

export async function deleteDistribution(distributionId: string): Promise<void> {
  await http.delete(`/cdn/distributions/${distributionId}`);
}

export async function listBehaviours(distributionId: string): Promise<Behaviour[]> {
  const { data } = await http.get<Behaviour[]>(
    `/cdn/distributions/${distributionId}/behaviours`,
  );
  return data;
}

export async function createBehaviour(
  distributionId: string,
  payload: BehaviourRequest,
): Promise<Behaviour> {
  const { data } = await http.post<Behaviour>(
    `/cdn/distributions/${distributionId}/behaviours`,
    payload,
  );
  return data;
}

/** A full replace: the API takes the same body as a create, plus an optional
 *  `position` that moves the behaviour. */
export async function updateBehaviour(
  distributionId: string,
  behaviourId: string,
  payload: BehaviourUpdateRequest,
): Promise<Behaviour> {
  const { data } = await http.put<Behaviour>(
    `/cdn/distributions/${distributionId}/behaviours/${behaviourId}`,
    payload,
  );
  return data;
}

export async function deleteBehaviour(
  distributionId: string,
  behaviourId: string,
): Promise<void> {
  await http.delete(`/cdn/distributions/${distributionId}/behaviours/${behaviourId}`);
}

export async function listInvalidations(distributionId: string): Promise<Invalidation[]> {
  const { data } = await http.get<Invalidation[]>(
    `/cdn/distributions/${distributionId}/invalidations`,
  );
  return data;
}

/** 202: accepted; it completes when the location has applied it. */
export async function createInvalidation(
  distributionId: string,
  payload: InvalidationCreateRequest,
): Promise<Invalidation> {
  const { data } = await http.post<Invalidation>(
    `/cdn/distributions/${distributionId}/invalidations`,
    payload,
  );
  return data;
}

export async function listDomains(distributionId: string): Promise<CustomDomain[]> {
  const { data } = await http.get<CustomDomain[]>(
    `/cdn/distributions/${distributionId}/domains`,
  );
  return data;
}

/** 201: the answer carries the DNS record to add. 503 with the reason while
 *  the cloud has no public delivery domain. */
export async function createDomain(
  distributionId: string,
  payload: CustomDomainCreateRequest,
): Promise<CustomDomain> {
  const { data } = await http.post<CustomDomain>(
    `/cdn/distributions/${distributionId}/domains`,
    payload,
  );
  return data;
}

/** Look the DNS record up again now; a failed domain starts over. */
export async function verifyDomain(
  distributionId: string,
  domainId: string,
): Promise<CustomDomain> {
  const { data } = await http.post<CustomDomain>(
    `/cdn/distributions/${distributionId}/domains/${domainId}/verify`,
  );
  return data;
}

/** A renewed certificate from Secrets, or a change of certificate mode. */
export async function replaceDomainCertificate(
  distributionId: string,
  domainId: string,
  payload: CustomDomainCertificateRequest,
): Promise<CustomDomain> {
  const { data } = await http.put<CustomDomain>(
    `/cdn/distributions/${distributionId}/domains/${domainId}/certificate`,
    payload,
  );
  return data;
}

export async function deleteDomain(distributionId: string, domainId: string): Promise<void> {
  await http.delete(`/cdn/distributions/${distributionId}/domains/${domainId}`);
}

/** The last `hours` (1 to 720) of per-minute series; never an error status
 *  for "nothing to draw" — that is `available: false` with the reason. */
export async function getDistributionMetrics(
  distributionId: string,
  hours: number,
): Promise<DistributionMetrics> {
  const { data } = await http.get<DistributionMetrics>(
    `/cdn/distributions/${distributionId}/metrics`,
    { params: { hours } },
  );
  return data;
}

/** One page of the distribution's requests, newest first. A cloud that keeps
 *  no access logs, or a log store that does not answer, is `available: false`
 *  with the reason — not an error status. */
export async function getDistributionLogs(
  distributionId: string,
  query: DistributionLogsQuery,
): Promise<DistributionLogs> {
  const { data } = await http.get<DistributionLogs>(
    `/cdn/distributions/${distributionId}/logs`,
    { params: query },
  );
  return data;
}

/** The same page as a CSV file, oldest first (the browser saves it). 503 when
 *  the logs cannot be read: a download cannot carry `available: false`. */
export async function downloadDistributionLogsCsv(
  distributionId: string,
  query: DistributionLogsQuery,
): Promise<string> {
  const { data } = await http.get<string>(`/cdn/distributions/${distributionId}/logs`, {
    params: { ...query, format: "csv" },
    responseType: "text",
  });
  return data;
}
